import React from "react";
import { useQuery } from "@apollo/client";
import { Flex, Text, Icon } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactComponent as LeftArrow } from "../../../assets/icons/left-arrow.svg";

import WithAppLayout from "../../../components/WithAppLayout";
import {
  GET_CANDIDATE_USER_QUERY,
  GET_CANDIDATE_PROFILE_ID_FOR_USER_QUERY,
} from "../../../graphql/agencies";
import { EDIT_CANDIDATE_PATH } from "../../../routes";
import UpdateCandidateProfileForm from "../../../components/UpdateCandidateProfileForm";
import UploadCandidateResumeForm from "../../../components/UploadCandidateResumeForm";
import AddCertificatesForm from "../../../components/AddCertificatesForm";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import UpdateCandidateRolePrefForm from "../../../components/UpdateCandidateRolePrefForm";
import { adjustIntercomWidget } from "../../../helpers/hooks";
import ViewCandidateDetails from "../../../components/ViewCandidateDetails";
import DeleteCandidateAccountButton from "../../../components/DeleteCandidateAccountButton";

function CandidateDetailsPage({ data, error, loading, refetch, currentUser }) {
  const candidateUser = data?.candidate;
  const navigate = useNavigate();

  if (error) {
    return null;
  }

  if (!candidateUser) {
    return null;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Flex h="100%" direction="column" flex="1">
      <Text
        onClick={() => {
          // if we can naviate to the previous page, do so
          // otherwise, navigate to the home page
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/");
          }
          adjustIntercomWidget();
        }}
        fontSize="md"
        lineHeight="6"
        fontWeight="normal"
        cursor="pointer"
        mb={4}
      >
        <Icon fontSize="10px" as={LeftArrow} /> Back
      </Text>
      <Flex direction="row" align="flex-start" justify="flex-start">
        <Flex direction="column" flex="1">
          <ViewCandidateDetails
            candidateReport={{
              candidate: candidateUser,
              favourite: candidateUser.favourite,
            }}
            showAgencyFavouriteAction={true}
            refetch={refetch}
          />
        </Flex>
        <Flex
          direction="column"
          flex="1"
          align="flex-start"
          justify="flex-start"
          gap="16"
          ml={10}
        >
          <UpdateCandidateProfileForm
            candidateUser={candidateUser}
            refetch={refetch}
          />
          <UpdateCandidateRolePrefForm
            candidateUser={candidateUser}
            refetch={refetch}
          />

          <AddCertificatesForm 
            candidateUser={candidateUser}
            refetch={refetch}
          />

          <UploadCandidateResumeForm
            candidateUser={candidateUser}
            refetch={refetch}
          />

          <DeleteCandidateAccountButton
            candidate={candidateUser}
            currentUser={currentUser}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

function PageComponent({ children, currentUser }) {
  // The route param is still named `:id`, but during the profile-key transition
  // it now carries the candidate's candidateProfileId. Older links carry the
  // candidate's user id — try the profile-keyed query first, and if it 404s,
  // resolve the user's profile id and redirect to the canonical URL so legacy
  // links keep working.
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery(GET_CANDIDATE_USER_QUERY, {
    variables: { candidateProfileId: id },
    fetchPolicy: "network-only",
  });

  const candidateNotFound = !loading && !!error && !data?.candidate;

  const { data: legacyData, loading: legacyLoading } = useQuery(
    GET_CANDIDATE_PROFILE_ID_FOR_USER_QUERY,
    {
      variables: { id },
      skip: !candidateNotFound,
      fetchPolicy: "network-only",
    }
  );

  const legacyProfileId = legacyData?.getUser?.candidateProfile?.id;

  React.useEffect(() => {
    if (legacyProfileId && legacyProfileId !== id) {
      navigate(EDIT_CANDIDATE_PATH.replace(":id", legacyProfileId), {
        replace: true,
      });
    }
  }, [legacyProfileId, id, navigate]);

  // Still resolving the candidate, or resolving a legacy user-id link before
  // the redirect lands.
  if (loading || (candidateNotFound && (legacyLoading || legacyProfileId))) {
    return <LoadingIndicator />;
  }

  return (
    <CandidateDetailsPage
      data={data}
      loading={loading}
      error={candidateNotFound && !legacyProfileId ? error : undefined}
      refetch={refetch}
      currentUser={currentUser}
    >
      {children}
    </CandidateDetailsPage>
  );
}

export default WithAppLayout(PageComponent, {
  showGradientBackground: true,
});
